"use client";

import type { ButtonHTMLAttributes, ReactNode, FC, Ref } from "react";
import { mergeClasses } from "@huub-web/utils";

import type { ClassNameProp, TrackingProps } from "../../props";
import { IconButton } from "../IconButton";

import { scalePulseIconStyle, inhaleGlowStyle } from "./AIAgentIconButton.css";

export type AIAgentIconButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> &
  ClassNameProp &
  TrackingProps & {
    /**
     * Children to render inside the button (e.g., loading indicator)
     */
    children?: ReactNode;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLButtonElement>;
  };

/**
 * AI Agent icon button with occasional scale pulse icon and gradient glow effect.
 * Pre-configured with AI-specific styling: gradient variant, rounded, header size.
 *
 * The underlying icon is decorative (`aria-hidden`), so the button needs an
 * explicit accessible name. A descriptive Dutch default is provided (SMA-1679,
 * accessibility) and can be overridden per consumer via the `aria-label` prop.
 */
export const AIAgentIconButton: FC<AIAgentIconButtonProps> = ({
  className,
  children,
  ref,
  "aria-label": ariaLabel = "Open AI-assistent",
  ...props
}) => (
  <IconButton
    ref={ref}
    icon="aiFilled"
    size="header"
    rounded
    variant="gradient"
    iconClassName={scalePulseIconStyle}
    className={mergeClasses(inhaleGlowStyle, className)}
    aria-label={ariaLabel}
    {...props}
  >
    {children}
  </IconButton>
);
